import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { API } from "../lib/api";

/**
 * Minimal markdown renderer — same flavour as HelpPage. Supports H1-H4, lists,
 * blockquotes, code spans, links, inline images, bold, and tables.
 */
function renderMarkdown(md) {
    const escape = (s) =>
        s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const inline = (s) =>
        escape(s)
            .replace(/`([^`]+)`/g, "<code>$1</code>")
            .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

    const lines = md.split("\n");
    const out = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (/^#{1,4}\s/.test(line)) {
            const level = line.match(/^#+/)[0].length;
            out.push(`<h${level}>${inline(line.replace(/^#+\s/, ""))}</h${level}>`);
            i++;
        } else if (/^---+$/.test(line.trim())) {
            out.push("<hr />");
            i++;
        } else if (/^[-*]\s/.test(line)) {
            const items = [];
            while (i < lines.length && /^[-*]\s/.test(lines[i])) {
                items.push(`<li>${inline(lines[i].replace(/^[-*]\s/, ""))}</li>`);
                i++;
            }
            out.push(`<ul>${items.join("")}</ul>`);
        } else if (/^\d+\.\s/.test(line)) {
            const items = [];
            while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
                items.push(`<li>${inline(lines[i].replace(/^\d+\.\s/, ""))}</li>`);
                i++;
            }
            out.push(`<ol>${items.join("")}</ol>`);
        } else if (/^>\s?/.test(line)) {
            out.push(`<blockquote>${inline(line.replace(/^>\s?/, ""))}</blockquote>`);
            i++;
        } else if (/^\|.*\|$/.test(line.trim())) {
            // basic table
            const rows = [];
            while (i < lines.length && /^\|.*\|$/.test(lines[i].trim())) {
                rows.push(lines[i].trim());
                i++;
            }
            if (rows.length >= 2 && /^[\s|:-]+$/.test(rows[1])) {
                const head = rows[0].slice(1, -1).split("|").map((c) => `<th>${inline(c.trim())}</th>`).join("");
                const body = rows.slice(2).map((r) =>
                    `<tr>${r.slice(1, -1).split("|").map((c) => `<td>${inline(c.trim())}</td>`).join("")}</tr>`
                ).join("");
                out.push(`<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`);
            } else {
                out.push(`<p>${inline(line)}</p>`);
            }
        } else if (line.trim() === "") {
            i++;
        } else {
            out.push(`<p>${inline(line)}</p>`);
            i++;
        }
    }
    return out.join("\n");
}

export default function LegalPage({ doc, title, description }) {
    const [md, setMd] = useState("");
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/legal/${doc}.md`)
            .then((r) => r.text())
            .then((t) => { if (!cancelled) { setMd(t); setLoading(false); } })
            .catch(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [doc]);

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO title={title} description={description} />
            <Header />
            <main className="max-w-3xl mx-auto px-4 md:px-8 py-12">
                <Link to="/" data-testid="legal-back-home" className="overline text-streetz-yellow inline-flex items-center gap-2 hover:text-white mb-8">
                    <ArrowLeft className="w-3 h-3" /> BACK HOME
                </Link>
                <article
                    data-testid={`legal-doc-${doc}`}
                    className="legal-doc text-white/90 leading-relaxed"
                    dangerouslySetInnerHTML={{
                        __html: loading
                            ? "<p>Loading…</p>"
                            : DOMPurify.sanitize(renderMarkdown(md)),
                    }}
                />
            </main>
            <Footer />
            <style>{`
                .legal-doc h1 { font-family: Anton, Impact, sans-serif; font-size: 2.5rem; line-height: 1.05; text-transform: uppercase; color: #fff; margin: 1.5rem 0 1rem; letter-spacing: .01em; }
                .legal-doc h2 { font-family: Anton, Impact, sans-serif; font-size: 1.6rem; text-transform: uppercase; color: #FFCC00; margin: 2rem 0 .75rem; }
                .legal-doc h3 { font-family: Anton, Impact, sans-serif; font-size: 1.25rem; text-transform: uppercase; color: #E11D2A; margin: 1.5rem 0 .5rem; }
                .legal-doc h4 { font-size: 1rem; text-transform: uppercase; color: #fff; margin: 1rem 0 .25rem; letter-spacing: .08em; }
                .legal-doc p { margin: .65rem 0; }
                .legal-doc ul, .legal-doc ol { padding-left: 1.5rem; margin: .65rem 0; }
                .legal-doc li { margin: .25rem 0; }
                .legal-doc hr { border: 0; border-top: 1px solid #262626; margin: 1.5rem 0; }
                .legal-doc a { color: #FFCC00; text-decoration: underline; }
                .legal-doc strong { color: #fff; }
                .legal-doc code { background: #110000; color: #FFCC00; padding: 1px 6px; font-size: 13px; }
                .legal-doc table { width: 100%; border-collapse: collapse; margin: 1rem 0; }
                .legal-doc th, .legal-doc td { border: 1px solid #262626; padding: 8px 12px; text-align: left; }
                .legal-doc th { background: #110000; color: #FFCC00; text-transform: uppercase; font-size: 12px; letter-spacing: .08em; }
                .legal-doc blockquote { border-left: 4px solid #E11D2A; background: #110000; padding: .75rem 1rem; margin: 1rem 0; color: #fff; }
            `}</style>
        </div>
    );
}
